import React, { useEffect, useState, useCallback } from 'react';
import {
  Timeline, Button, Modal, Form, Input, Select, DatePicker,
  Popconfirm, message, Tag, Typography, Empty, Space
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { eventsApi } from '../api';
import { SheepEvent, EventType } from '../types';

const EVENT_META: Record<EventType, { label: string; color: string; icon: string }> = {
  vaccination: { label: 'Прививка',    color: 'blue',    icon: '💉' },
  treatment:   { label: 'Лечение',     color: 'orange',  icon: '🩺' },
  injury:      { label: 'Травма',      color: 'red',     icon: '🦴' },
  weight:      { label: 'Взвешивание', color: 'purple',  icon: '⚖️' },
  birth:       { label: 'Рождение',    color: 'green',   icon: '🐣' },
  sale:        { label: 'Продажа',     color: 'cyan',    icon: '💰' },
  other:       { label: 'Другое',      color: 'default', icon: '📝' },
};

interface Props {
  sheepId: number;
  readOnly?: boolean;
}

export default function EventsJournal({ sheepId, readOnly = false }: Props) {
  const [events, setEvents] = useState<SheepEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SheepEvent | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = readOnly ? eventsApi.listPublic : eventsApi.list;
      const r = await fn(sheepId);
      setEvents(r.data);
    } finally {
      setLoading(false);
    }
  }, [sheepId, readOnly]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: 'other', eventDate: dayjs() });
    setModalOpen(true);
  };

  const openEdit = (ev: SheepEvent) => {
    setEditing(ev);
    form.setFieldsValue({ ...ev, eventDate: dayjs(ev.eventDate) });
    setModalOpen(true);
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const payload = { ...values, eventDate: values.eventDate.format('YYYY-MM-DD') };
      if (editing) {
        await eventsApi.update(editing.id, payload);
        message.success('Запись обновлена');
      } else {
        await eventsApi.create(sheepId, payload);
        message.success('Запись добавлена');
      }
      setModalOpen(false);
      load();
    } catch {
      message.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await eventsApi.remove(id);
    message.success('Удалено');
    load();
  };

  if (loading) return null;

  return (
    <div>
      {!readOnly && (
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Добавить запись
          </Button>
        </div>
      )}

      {events.length === 0 ? (
        <Empty description="Записей пока нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Timeline
          items={events.map((ev) => {
            const meta = EVENT_META[ev.type];
            return {
              color: meta.color,
              children: (
                <div style={{
                  background: '#fafafa', borderRadius: 8, padding: '10px 14px',
                  border: '1px solid #f0f0f0',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <Space size={6} style={{ marginBottom: 4 }}>
                        <span>{meta.icon}</span>
                        <Tag color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>
                        <Typography.Text strong>{ev.title}</Typography.Text>
                      </Space>
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {new Date(ev.eventDate).toLocaleDateString('ru-RU')}
                          {ev.performedBy && ` · ${ev.performedBy}`}
                        </Typography.Text>
                      </div>
                      {ev.description && (
                        <Typography.Text style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
                          {ev.description}
                        </Typography.Text>
                      )}
                    </div>
                    {!readOnly && (
                      <Space size={4}>
                        <Button size="small" type="text" icon={<EditOutlined />}
                          onClick={() => openEdit(ev)} />
                        <Popconfirm title="Удалить запись?" onConfirm={() => handleDelete(ev.id)}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    )}
                  </div>
                </div>
              ),
            };
          })}
        />
      )}

      <Modal
        title={editing ? 'Редактировать запись' : 'Новая запись'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 8 }}>
          <Form.Item name="type" label="Тип события" rules={[{ required: true }]}>
            <Select>
              {(Object.keys(EVENT_META) as EventType[]).map((k) => (
                <Select.Option key={k} value={k}>
                  {EVENT_META[k].icon} {EVENT_META[k].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="title" label="Заголовок" rules={[{ required: true, message: 'Введите заголовок' }]}>
            <Input placeholder="Вакцина от ящура, Перелом левой ноги..." />
          </Form.Item>
          <Form.Item name="eventDate" label="Дата события" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="performedBy" label="Кто выполнил">
            <Input placeholder="Ветеринар Иванов А.А." />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} placeholder="Дополнительные подробности..." />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Отмена</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editing ? 'Сохранить' : 'Добавить'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
